import { motion } from "framer-motion";

// Subtle, looping mini-illustrations for each agent.
// All visuals share a square-ish viewBox (320 x 280) and live on the right side of the featured card.

const FRAME = "320 0 320 280"; // we draw inside 0..320 wide; pad-left via viewBox
const Box = ({ children, testid }) => (
    <div
        data-testid={testid}
        className="relative w-full h-full rounded-2xl bg-gradient-to-br from-[var(--cream-2)] via-[var(--paper)] to-[var(--cream-2)] border border-[var(--border)] overflow-hidden"
    >
        {/* faint grid */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.35]" aria-hidden>
            <defs>
                <pattern id="agent-grid" width="22" height="22" patternUnits="userSpaceOnUse">
                    <path d="M 22 0 L 0 0 0 22" fill="none" stroke="var(--border)" strokeWidth="0.6" />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#agent-grid)" />
        </svg>
        <div className="relative w-full h-full">{children}</div>
    </div>
);

/* ───────── 1. Intelligent Reception — incoming call routes to lanes ───────── */
const ReceptionVisual = () => (
    <Box testid="agent-visual-reception">
        <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
            {/* phone hub */}
            <motion.circle
                cx="160" cy="60" r="22"
                fill="var(--blue-soft)" stroke="var(--blue-deep)" strokeWidth="1.5"
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            />
            <text x="160" y="65" textAnchor="middle" fontSize="16" fill="var(--blue-deep)" fontFamily="system-ui">📞</text>

            {/* ping ring */}
            <motion.circle
                cx="160" cy="60" r="22" fill="none" stroke="var(--blue)" strokeWidth="1.5"
                initial={{ opacity: 0.7, scale: 1 }}
                animate={{ opacity: 0, scale: 2.2 }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
            />

            {/* routing lines */}
            {[
                { x: 70, label: "Intake", delay: 0.0 },
                { x: 160, label: "Case Mgr", delay: 0.4 },
                { x: 250, label: "Paralegal", delay: 0.8 },
            ].map((lane, i) => (
                <g key={lane.label}>
                    <motion.path
                        d={`M 160 82 Q 160 130 ${lane.x} 170`}
                        stroke="var(--blue)" strokeWidth="1.5" fill="none" strokeDasharray="4 4"
                        initial={{ pathLength: 0, opacity: 0.4 }}
                        animate={{ pathLength: 1, opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 2.2, repeat: Infinity, delay: lane.delay, ease: "easeInOut" }}
                    />
                    {/* lane card */}
                    <motion.rect
                        x={lane.x - 38} y={175} width="76" height="42" rx="10"
                        fill="var(--paper)" stroke="var(--border-strong)" strokeWidth="1"
                        animate={{ y: [175, 173, 175] }}
                        transition={{ duration: 2.2, repeat: Infinity, delay: lane.delay + 0.6, ease: "easeInOut" }}
                    />
                    <text x={lane.x} y={199} textAnchor="middle" fontSize="11" fill="var(--ink)" fontFamily="ui-monospace, monospace">
                        {lane.label}
                    </text>
                    <motion.circle
                        cx={lane.x + 26} cy={185} r="3"
                        fill="var(--blue)"
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{ duration: 2.2, repeat: Infinity, delay: lane.delay + 0.6 }}
                    />
                </g>
            ))}

            {/* footer hint */}
            <text x="160" y="252" textAnchor="middle" fontSize="9" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="2">
                ROUTING · LIVE
            </text>
        </svg>
    </Box>
);

/* ───────── 2. Lead Qualification — funnel filtering droplets ───────── */
const QualificationVisual = () => (
    <Box testid="agent-visual-qualification">
        <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
            {/* droplets falling */}
            {[0, 1, 2, 3, 4].map((i) => (
                <motion.circle
                    key={i}
                    cx={110 + i * 25} r="5"
                    fill={i % 2 === 0 ? "var(--blue)" : "var(--ink-dim)"}
                    initial={{ cy: 20, opacity: 0 }}
                    animate={{ cy: [20, 110], opacity: [0, 1, 1, 0] }}
                    transition={{
                        duration: 2.2,
                        repeat: Infinity,
                        delay: i * 0.35,
                        ease: "easeIn",
                    }}
                />
            ))}

            {/* funnel */}
            <path
                d="M 90 110 L 230 110 L 180 170 L 180 215 L 140 215 L 140 170 Z"
                fill="var(--paper)" stroke="var(--ink)" strokeWidth="1.8"
            />
            <line x1="90" y1="110" x2="230" y2="110" stroke="var(--blue-deep)" strokeWidth="2" />

            {/* qualified droplets emerging */}
            {[0, 1, 2].map((i) => (
                <motion.circle
                    key={`q-${i}`}
                    cx="160" r="4"
                    fill="var(--blue)"
                    initial={{ cy: 215, opacity: 0 }}
                    animate={{ cy: [215, 250], opacity: [1, 1, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, delay: 1 + i * 0.5, ease: "easeIn" }}
                />
            ))}
            <text x="160" y="255" textAnchor="middle" fontSize="9" fill="var(--blue-deep)" fontFamily="ui-monospace, monospace" letterSpacing="2">
                QUALIFIED
            </text>
        </svg>
    </Box>
);

/* ───────── 3. Matter Completion — checklist filling in ───────── */
const CompletionVisual = () => {
    const rows = ["Client info", "Incident date", "Insurer", "Adjuster #", "Medical records"];
    return (
        <Box testid="agent-visual-completion">
            <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
                <rect x="60" y="32" width="200" height="216" rx="14" fill="var(--paper)" stroke="var(--border-strong)" strokeWidth="1.2" />
                <text x="76" y="56" fontSize="11" fontFamily="ui-monospace, monospace" fill="var(--ink-dim)" letterSpacing="2">
                    MATTER · #2841
                </text>
                <line x1="76" y1="68" x2="244" y2="68" stroke="var(--border)" strokeWidth="1" />

                {rows.map((label, i) => (
                    <g key={label}>
                        <motion.rect
                            x="76" y={88 + i * 30} width="16" height="16" rx="4"
                            fill="var(--paper)"
                            stroke="var(--ink-dim)" strokeWidth="1.2"
                            animate={{
                                fill: ["var(--paper)", "var(--paper)", "var(--blue)"],
                                stroke: ["var(--ink-dim)", "var(--ink-dim)", "var(--blue-deep)"],
                            }}
                            transition={{ duration: 5, repeat: Infinity, delay: i * 0.7, times: [0, 0.6, 1] }}
                        />
                        <motion.path
                            d={`M ${80} ${96 + i * 30} l 3 4 l 7 -7`}
                            stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"
                            initial={{ opacity: 0, pathLength: 0 }}
                            animate={{ opacity: [0, 0, 1], pathLength: [0, 0, 1] }}
                            transition={{ duration: 5, repeat: Infinity, delay: i * 0.7, times: [0, 0.6, 0.9] }}
                        />
                        <text x="102" y={100 + i * 30} fontSize="12" fill="var(--ink)" fontFamily="system-ui">
                            {label}
                        </text>
                    </g>
                ))}
            </svg>
        </Box>
    );
};

/* ───────── 4. Chaser — calendar with bouncing reminder pings ───────── */
const ChaserVisual = () => (
    <Box testid="agent-visual-chaser">
        <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
            <rect x="70" y="40" width="180" height="200" rx="12" fill="var(--paper)" stroke="var(--border-strong)" strokeWidth="1.2" />
            <rect x="70" y="40" width="180" height="32" rx="12" fill="var(--ink)" />
            <text x="160" y="61" textAnchor="middle" fontSize="11" fill="white" fontFamily="ui-monospace, monospace" letterSpacing="3">
                FOLLOW-UP
            </text>

            {/* grid cells */}
            {Array.from({ length: 20 }).map((_, i) => {
                const col = i % 5;
                const row = Math.floor(i / 5);
                const x = 82 + col * 32;
                const y = 86 + row * 30;
                const isActive = [3, 6, 12, 17].includes(i);
                return (
                    <g key={`cell-${x}-${y}`}>
                        <rect x={x} y={y} width="26" height="22" rx="5" fill="var(--cream-2)" stroke="var(--border)" strokeWidth="0.8" />
                        {isActive && (
                            <motion.circle
                                cx={x + 13} cy={y + 11} r="4"
                                fill="var(--blue)"
                                animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }}
                                transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.15 }}
                            />
                        )}
                    </g>
                );
            })}

            {/* outgoing arrow */}
            <motion.g
                animate={{ x: [0, 6, 0], opacity: [1, 0.7, 1] }}
                transition={{ duration: 1.6, repeat: Infinity }}
            >
                <path d="M 100 232 L 220 232" stroke="var(--blue-deep)" strokeWidth="1.5" />
                <path d="M 215 228 L 222 232 L 215 236" stroke="var(--blue-deep)" strokeWidth="1.5" fill="none" />
            </motion.g>
            <text x="160" y="225" textAnchor="middle" fontSize="9" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="2">
                CALL · SMS · EMAIL
            </text>
        </svg>
    </Box>
);

/* ───────── 5. Document Follow-up — signature being drawn ───────── */
const DocumentVisual = () => (
    <Box testid="agent-visual-document">
        <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
            {/* document */}
            <rect x="90" y="40" width="140" height="180" rx="6" fill="var(--paper)" stroke="var(--border-strong)" strokeWidth="1.2" />
            <path d="M 200 40 L 230 70 L 200 70 Z" fill="var(--cream-2)" stroke="var(--border-strong)" strokeWidth="1.2" />

            {/* doc text lines */}
            {[0, 1, 2, 3, 4].map((i) => (
                <rect key={i} x="104" y={82 + i * 14} width={i === 4 ? 60 : 110} height="4" rx="2" fill="var(--border-strong)" opacity="0.5" />
            ))}

            {/* signature line */}
            <line x1="104" y1="180" x2="216" y2="180" stroke="var(--ink-dim)" strokeWidth="1" strokeDasharray="3 3" />
            <text x="104" y="194" fontSize="8" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="2">
                SIGNATURE
            </text>

            {/* animated signature */}
            <motion.path
                d="M 110 174 C 120 162, 130 184, 140 170 S 160 160, 175 174 S 195 168, 210 172"
                stroke="var(--blue-deep)"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: [0, 1, 1, 0] }}
                transition={{ duration: 3.4, repeat: Infinity, times: [0, 0.5, 0.8, 1], ease: "easeInOut" }}
            />

            {/* checkmark badge */}
            <motion.g
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: [0, 0, 1, 1, 0], scale: [0.7, 0.7, 1, 1, 0.7] }}
                transition={{ duration: 3.4, repeat: Infinity, times: [0, 0.5, 0.65, 0.85, 1] }}
            >
                <circle cx="222" cy="56" r="14" fill="var(--blue)" />
                <path d="M 216 56 l 4 4 l 8 -8" stroke="white" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </motion.g>
        </svg>
    </Box>
);

/* ───────── 6. Medical Records — pulse + folder ───────── */
const MedicalVisual = () => (
    <Box testid="agent-visual-medical">
        <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
            {/* folder */}
            <path d="M 75 78 L 75 230 L 245 230 L 245 90 L 165 90 L 155 78 Z" fill="var(--paper)" stroke="var(--border-strong)" strokeWidth="1.4" />
            <rect x="95" y="108" width="130" height="20" rx="4" fill="var(--cream-2)" />
            <text x="105" y="121" fontSize="10" fill="var(--ink)" fontFamily="ui-monospace, monospace">MED-RCD #</text>
            <text x="200" y="121" fontSize="10" fill="var(--blue-deep)" fontFamily="ui-monospace, monospace">42-718</text>

            {/* pulse line */}
            <rect x="95" y="140" width="130" height="62" rx="4" fill="var(--ink)" opacity="0.92" />
            <motion.path
                d="M 100 170 L 118 170 L 124 152 L 132 188 L 140 160 L 150 175 L 222 175"
                stroke="var(--blue)" strokeWidth="2" fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: [0, 1], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.6, repeat: Infinity, times: [0, 0.5, 0.85, 1], ease: "easeInOut" }}
            />
            {/* moving dot */}
            <motion.circle
                r="3" fill="var(--blue)"
                initial={{ cx: 100, cy: 170 }}
                animate={{
                    cx: [100, 118, 124, 132, 140, 150, 222],
                    cy: [170, 170, 152, 188, 160, 175, 175],
                }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* HIPAA tag */}
            <text x="160" y="222" textAnchor="middle" fontSize="9" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="3">
                HIPAA · SECURE
            </text>
        </svg>
    </Box>
);

/* ───────── 7. Claims Opening — form fields auto-filling ───────── */
const ClaimsVisual = () => {
    const fields = [
        { label: "Carrier", value: "Allstate", delay: 0 },
        { label: "Policy #", value: "P-48217", delay: 0.5 },
        { label: "DOL", value: "2026-01-14", delay: 1.0 },
        { label: "Claim #", value: "C-9120", delay: 1.5 },
    ];
    return (
        <Box testid="agent-visual-claims">
            <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
                <rect x="60" y="36" width="200" height="208" rx="12" fill="var(--paper)" stroke="var(--border-strong)" strokeWidth="1.2" />
                <text x="76" y="58" fontSize="10" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="2">
                    FNOL · CLAIM
                </text>
                <line x1="76" y1="68" x2="244" y2="68" stroke="var(--border)" strokeWidth="1" />

                {fields.map((f, i) => (
                    <g key={f.label}>
                        <text x="76" y={94 + i * 36} fontSize="10" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="2">
                            {f.label.toUpperCase()}
                        </text>
                        <rect x="76" y={100 + i * 36} width="168" height="22" rx="5" fill="var(--cream-2)" stroke="var(--border)" strokeWidth="1" />
                        <motion.text
                            x="84" y={115 + i * 36} fontSize="12" fill="var(--ink)" fontFamily="ui-monospace, monospace"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 0, 1, 1, 0] }}
                            transition={{ duration: 6, repeat: Infinity, delay: f.delay, times: [0, 0.2, 0.3, 0.85, 1] }}
                        >
                            {f.value}
                        </motion.text>
                        {/* typing caret */}
                        <motion.rect
                            y={104 + i * 36} width="1.6" height="14" fill="var(--blue-deep)"
                            initial={{ x: 84, opacity: 0 }}
                            animate={{
                                x: [84, 84 + f.value.length * 7],
                                opacity: [0, 1, 1, 0, 0],
                            }}
                            transition={{ duration: 6, repeat: Infinity, delay: f.delay, times: [0, 0.2, 0.3, 0.35, 1] }}
                        />
                    </g>
                ))}
            </svg>
        </Box>
    );
};

/* ───────── 8. Adjuster — chat bubbles ping-pong ───────── */
const AdjusterVisual = () => (
    <Box testid="agent-visual-adjuster">
        <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
            <text x="76" y="46" fontSize="10" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="2">
                ADJUSTER · KAREN B.
            </text>

            {/* incoming bubble */}
            <motion.g
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: [0, 1, 1, 1, 1], x: [-20, 0, 0, 0, 0] }}
                transition={{ duration: 5, repeat: Infinity, times: [0, 0.1, 0.4, 0.6, 1], delay: 0 }}
            >
                <rect x="74" y="62" width="170" height="40" rx="14" fill="var(--paper)" stroke="var(--border-strong)" strokeWidth="1" />
                <text x="86" y="80" fontSize="11" fill="var(--ink)" fontFamily="system-ui">Need updated demand</text>
                <text x="86" y="94" fontSize="11" fill="var(--ink-soft)" fontFamily="system-ui">by Friday.</text>
            </motion.g>

            {/* outgoing bubble (agent reply) */}
            <motion.g
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: [0, 0, 1, 1, 1], x: [20, 20, 0, 0, 0] }}
                transition={{ duration: 5, repeat: Infinity, times: [0, 0.4, 0.55, 0.85, 1], delay: 0 }}
            >
                <rect x="84" y="118" width="160" height="40" rx="14" fill="var(--blue)" />
                <text x="96" y="136" fontSize="11" fill="white" fontFamily="system-ui">Sending updated demand</text>
                <text x="96" y="150" fontSize="11" fill="white" fontFamily="system-ui">+ medical bundle now.</text>
            </motion.g>

            {/* logged badge */}
            <motion.g
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: [0, 0, 0, 1, 0], y: [10, 10, 10, 0, 0] }}
                transition={{ duration: 5, repeat: Infinity, times: [0, 0.4, 0.7, 0.85, 1], delay: 0 }}
            >
                <rect x="86" y="184" width="140" height="32" rx="8" fill="var(--ink)" />
                <circle cx="100" cy="200" r="4" fill="var(--blue)" />
                <text x="112" y="204" fontSize="10" fill="white" fontFamily="ui-monospace, monospace" letterSpacing="2">
                    LOGGED TO CASE
                </text>
            </motion.g>

            <text x="160" y="246" textAnchor="middle" fontSize="9" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="3">
                CALL · EMAIL · LOGGED
            </text>
        </svg>
    </Box>
);

/* ───────── 9. Cooking new agents — workshop/forge visual ───────── */
const CookingVisual = () => {
    const inventory = [
        { label: "Demand Letter", delay: 0 },
        { label: "Settlement Calc", delay: 0.4 },
        { label: "Deposition Prep", delay: 0.8 },
        { label: "Lien Resolution", delay: 1.2 },
    ];
    return (
        <Box testid="agent-visual-cooking">
            <svg viewBox="0 0 320 280" className="absolute inset-0 w-full h-full">
                {/* workshop bench */}
                <rect x="40" y="190" width="240" height="3" fill="var(--ink)" opacity="0.55" />
                <text x="160" y="248" textAnchor="middle" fontSize="9" fill="var(--ink-dim)" fontFamily="ui-monospace, monospace" letterSpacing="3">
                    HANDCRAFTED · SHIPPING SOON
                </text>

                {/* beaker / flask */}
                <g transform="translate(120 60)">
                    <path
                        d="M 30 0 L 50 0 L 50 30 L 70 80 Q 40 95 10 80 L 30 30 Z"
                        fill="var(--paper)"
                        stroke="var(--ink)"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                    />
                    {/* liquid */}
                    <motion.path
                        d="M 18 60 Q 40 70 62 60 L 70 80 Q 40 95 10 80 Z"
                        fill="var(--blue)"
                        animate={{ opacity: [0.8, 1, 0.8] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* bubbles */}
                    {[0, 1, 2].map((i) => (
                        <motion.circle
                            key={i}
                            r="3"
                            fill="white"
                            opacity="0.85"
                            initial={{ cx: 30 + i * 12, cy: 78 }}
                            animate={{ cy: [78, 50], opacity: [0, 1, 0] }}
                            transition={{
                                duration: 1.8,
                                repeat: Infinity,
                                delay: i * 0.6,
                                ease: "easeOut",
                            }}
                        />
                    ))}
                    {/* steam */}
                    {[0, 1].map((i) => (
                        <motion.path
                            key={`s-${i}`}
                            d={`M ${28 + i * 16} 5 Q ${24 + i * 16} -5 ${30 + i * 16} -15`}
                            stroke="var(--ink-dim)"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            fill="none"
                            opacity="0.6"
                            initial={{ opacity: 0, y: 0 }}
                            animate={{ opacity: [0, 0.6, 0], y: [0, -12, -24] }}
                            transition={{
                                duration: 2.2,
                                repeat: Infinity,
                                delay: i * 0.5,
                                ease: "easeOut",
                            }}
                        />
                    ))}
                </g>

                {/* inventory chips next to flask, rotating in */}
                {inventory.map((item, i) => (
                    <motion.g
                        key={item.label}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: [0, 1, 1, 0], x: [-10, 0, 0, 8] }}
                        transition={{
                            duration: 6,
                            repeat: Infinity,
                            delay: item.delay,
                            times: [0, 0.1, 0.85, 1],
                        }}
                    >
                        <rect
                            x={30}
                            y={210 + (i % 2) * 18 - 30}
                            width="90"
                            height="14"
                            rx="7"
                            fill="var(--paper)"
                            stroke="var(--border-strong)"
                            strokeWidth="0.8"
                        />
                        <text
                            x={75}
                            y={221 + (i % 2) * 18 - 30}
                            textAnchor="middle"
                            fontSize="9"
                            fill="var(--ink)"
                            fontFamily="ui-monospace, monospace"
                        >
                            {item.label}
                        </text>
                    </motion.g>
                ))}

                {/* pulsing badge "+ NEW" floating above */}
                <motion.g
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                >
                    <rect x="216" y="40" width="62" height="22" rx="11" fill="var(--blue)" />
                    <text x="247" y="55" textAnchor="middle" fontSize="11" fill="white" fontFamily="ui-monospace, monospace" letterSpacing="2" fontWeight="600">
                        + NEW
                    </text>
                </motion.g>
                <motion.circle
                    cx="247" cy="51" r="14"
                    fill="none" stroke="var(--blue)" strokeWidth="1.4"
                    initial={{ opacity: 0.6, scale: 1 }}
                    animate={{ opacity: 0, scale: 2.4 }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                />
            </svg>
        </Box>
    );
};

const VISUALS = {
    "Intelligent Reception": ReceptionVisual,
    "Lead Qualification": QualificationVisual,
    "Matter Completion": CompletionVisual,
    "Chaser Agent": ChaserVisual,
    "Document Follow-up": DocumentVisual,
    "Medical Records Retrieval": MedicalVisual,
    "Claims Opening": ClaimsVisual,
    "Adjuster Agent": AdjusterVisual,
    "More shipping every month": CookingVisual,
};

const AgentVisual = ({ name }) => {
    const V = VISUALS[name];
    if (!V) return null;
    return <V />;
};

export default AgentVisual;
